<script setup lang="ts">
import EzTable from '@/corePagesModule/components/ezTable/EzTable.vue';
import InfoModal from '@/corePagesModule/components/InfoModal.vue';
import { i18n } from '@/i18n';
import { ERROR_REASON_LABELS, TABLE_HEADERS } from '../constants';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { storeToRefs } from 'pinia';
import { useUnmatchedOrderStore } from '../pages/useUnmatchedOrderStore';
import { computed } from 'vue';
import EzHeader from '@/corePagesModule/components/ezTable/EzHeader.vue';
import EzTableRow from '@/corePagesModule/components/ezTable/EzTableRow.vue';
import EzCell from '@/corePagesModule/components/ezTable/EzCell.vue';
import OrderStatusBadge from '@/corePagesModule/components/OrderStatusBadge.vue';

const t = i18n.t;
const store = useUnmatchedOrderStore();
const { state } = storeToRefs(store);
const { bulkRegisterOrderBusinesses } = store;

const displayHeaders = computed(() => TABLE_HEADERS.slice(1, -1));
const displayItems = computed(() => state.value.items.filter((i) => state.value.checkedIds.includes(i.unmatchedOrderId)));

/**
 * キャンセルボタンを押した際の処理
 */
const onBack = () => {
  state.value.bulkRegisterModalState = false;
};
</script>
<template>
  <InfoModal
    :modal-state="state.bulkRegisterModalState"
    :modal-width="1300"
    :back-button-label="t('キャンセル')"
    :next-button-label="t('登録')"
    @on-back="onBack"
    @on-next="bulkRegisterOrderBusinesses"
  >
    <template #title>{{ t('発注一括登録') }}</template>
    <template #description>
      {{ t('未照合発注を一括で発注登録します。') }}
      {{ t('なお、必須事項を満たしていないものや、重複しているものなどは自動的に除外されます。') }}
    </template>
    <template #default>
      <div class="content__container">
        <div class="content__title">
          {{ t('登録する未照合発注一覧') }}
        </div>
        <EzTable :grid-template-columns="displayHeaders.map((h) => h.width)" sticky-header max-height="calc(100vh - 600px)">
          <template #header>
            <EzHeader v-for="header in displayHeaders" :key="`header:${header.key}`">
              {{ t(header.label) }}
            </EzHeader>
          </template>
          <template #body>
            <EzTableRow
              v-for="(item, index) in displayItems"
              :key="item.unmatchedOrderId"
              :grid-template-columns="displayHeaders.map((h) => h.width)"
              class="unmatched-row"
            >
              <template v-for="header in displayHeaders" :key="`row:${header.key}:${index}`">
                <EzCell v-if="header.key === 'orderStatus'" force-center>
                  <span class="order-status">
                    <OrderStatusBadge :order-status-str="item.orderStatus" />
                  </span>
                </EzCell>
                <EzCell v-else-if="header.key === 'errorReason'">
                  <span class="error-reason">{{ t(ERROR_REASON_LABELS[item.errorReason] ?? ERROR_REASON_LABELS.unknown) }}</span>
                </EzCell>
                <EzCell v-else-if="header.type === 'num'" is-number>
                  {{ item[header.key]?.toLocaleString() ?? '' }}
                </EzCell>
                <EzCell v-else-if="header.type === 'date'" is-number>
                  {{ item[header.key] ? DateService.convertMSUnixTimeToYYYYMMDDSlash(item[header.key]) : '' }}
                </EzCell>
                <EzCell v-else-if="header.type === 'str'" is-number>
                  {{ item[header.key] ?? '' }}
                </EzCell>
              </template>
            </EzTableRow>
          </template>
        </EzTable>
      </div>
    </template>
  </InfoModal>
</template>
<style lang="scss" scoped>
.content {
  &__container {
    * {
      font-family: 'Noto Sans JP';
    }
    display: grid;
    gap: 8px;
    padding: 8px;
    border: 1px solid $borderDefault;
    border-radius: 8px;
    background-color: $semanticWhite;
  }
  &__title {
    padding: 8px;
    font-size: 16px;
  }
}

:deep(.ez-table .table__header-container) {
  width: 100%;
}

:deep(.table__header) {
  min-height: 36px;
}

:deep(.table__header__content) {
  line-height: 36px;
}

:deep(.table__cell) {
  border-bottom: 1px solid $surfaceSecondary;
}

:deep(.table__cell__content) {
  padding-top: 4px;
  padding-bottom: 4px;
}

.error-reason {
  color: $semanticError;
  font-family: Noto Sans JP;
  font-size: 12px;
  font-weight: 500;
  line-height: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
